<template>
    <div>
        <!-- Loading state -->
        <div v-if="loading" class="mt-2 flex items-center gap-1.5 text-xs text-slate-400">
            <svg class="animate-spin h-3 w-3 shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            Checking status...
        </div>

        <!-- Already connected (issue was resolved before user opened notification) -->
        <div
            v-else-if="connectionStatus === 'CONNECTED'"
            class="mt-2 flex items-center gap-2 rounded-md bg-green-50 px-3 py-1.5"
        >
            <svg class="h-3.5 w-3.5 shrink-0 text-green-500" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M20 6L9 17l-5-5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
            <span class="text-xs font-medium text-green-700">Connection resolved</span>
        </div>

        <!-- Needs action -->
        <div
            v-else-if="connectionStatus && hasActionableStatus"
            class="mt-2 flex items-center gap-2 rounded-md bg-red-50 px-3 py-1.5"
        >
            <svg class="h-3.5 w-3.5 shrink-0 text-red-500" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" />
                <path d="M12 8v4m0 4h.01" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
            </svg>
            <span class="text-xs text-red-700 flex-1">
                <span v-if="memberDisplayName" class="font-medium">{{ memberDisplayName }} · </span>{{ statusMessage }}
            </span>
            <button
                @click="handleResolve"
                :disabled="loadingResolve"
                class="shrink-0 rounded border border-red-200 bg-white dark:bg-slate-800 px-2.5 py-1 text-xs font-medium text-slate-800 shadow-sm transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
                <svg v-if="loadingResolve" class="inline animate-spin h-3 w-3 mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Resolve
            </button>
        </div>

        <!-- Informational only — no action available -->
        <div
            v-else-if="connectionStatus && !hasActionableStatus"
            class="mt-2 flex items-center gap-2 rounded-md bg-amber-50 px-3 py-1.5"
        >
            <svg class="h-3.5 w-3.5 shrink-0 text-amber-500" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2zm0 9a1 1 0 10-2 0v4a1 1 0 102 0v-4zm-1-3a1 1 0 112 0 1 1 0 01-2 0z" fill="currentColor" />
            </svg>
            <span class="text-xs text-amber-700">
                <span v-if="memberDisplayName" class="font-medium">{{ memberDisplayName }} · </span>{{ statusMessage }}
            </span>
        </div>

        <!-- Fallback: live status fetch failed or returned nothing — link to the notification's route so the user always has a path to resolve -->
        <div v-else-if="notification.route" class="mt-3">
            <button
                type="button"
                @click="handleFallbackClick"
                class="inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white dark:bg-slate-800 px-3 py-1.5 text-sm font-medium text-slate-800 shadow-sm transition hover:bg-slate-50"
            >
                {{ notification.cta_label || 'Review issue' }}
                <i class="far fa-arrow-right text-[10px]"></i>
            </button>
        </div>

        <!-- Modals rendered outside the dropdown via Teleport -->
        <Teleport to="body">
            <ConnectModal
                v-if="localMember && membersStore.wallet"
                :wallet="membersStore.wallet"
                :showAddCardModal="localShowConnectModal"
                :cards="[]"
                :users="filteredUsers"
                :banks="membersStore.banks"
                :step="1"
                :connectType="'memberRequest'"
                :member="localMember"
                @close="handleConnectModalClose"
                @addedCard="handleConnectSuccess"
            />

            <ChallengesModal
                v-if="localMember"
                :member="localMember"
                :isOpen="localShowChallengeModal"
                @close="handleChallengeModalClose"
                @success="handleChallengeSuccess"
            />
        </Teleport>
    </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { router } from '@inertiajs/vue3';
import { useMembersStore } from '@/store/members';
import ConnectModal from '@/components/wallet/modals/connect.vue';
import ChallengesModal from '@/components/wallet/modals/challenges.vue';

const props = defineProps({
    notification: {
        type: Object,
        required: true,
    },
});

const emit = defineEmits(['resolved']);

const membersStore = useMembersStore();

const loading = ref(false);
const loadingResolve = ref(false);
const connectionStatus = ref(null);
const memberDisplayName = ref(null);
const localMember = ref(null);
const localShowConnectModal = ref(false);
const localShowChallengeModal = ref(false);

const ACTIONABLE_STATUSES = ['CHALLENGED', 'REJECTED', 'EXPIRED', 'DENIED', 'FAILED', 'DISCONNECTED', 'PREVENTED', 'IMPAIRED'];

const hasActionableStatus = computed(() => {
    return ACTIONABLE_STATUSES.includes(connectionStatus.value);
});

const statusMessage = computed(() => {
    const messages = {
        CHALLENGED: 'Authentication challenge required',
        REJECTED: 'Connection was rejected',
        EXPIRED: 'Connection has expired',
        DENIED: 'Connection was denied',
        FAILED: 'Connection failed',
        DISCONNECTED: 'Connection disconnected',
        PREVENTED: 'Last 3 attempts failed — re-enter credentials',
        IMPAIRED: 'Re-authentication required',
        LOCKED: 'Account locked — visit institution website to resolve',
        IMPEDED: 'Attention needed at institution website',
        DEGRADED: 'Connection upgrading — try again later',
        DISCONTINUED: 'Connections to this institution are no longer supported',
        CLOSED: 'This connection has been closed',
        DISABLED: 'Data import from this institution is disabled',
    };
    return messages[connectionStatus.value] || 'Connection issue — review needed';
});

const filteredUsers = computed(() => {
    return (membersStore.users || []).filter((u) => u.show);
});

const fetchMemberStatus = async () => {
    if (!props.notification.member_guid) {
        return;
    }

    loading.value = true;

    // Cap the wait at 3s. The MemberController calls MX, which can hang up
    // to 10s when MX is slow. Without this cap, multiple resolvers on the
    // page would each block for 10s, making the page feel frozen.
    const timeout = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('timeout')), 3000)
    );

    try {
        const response = await Promise.race([
            ApiService.apiCall(
                `/members/${props.notification.member_guid}`,
                'get'
            ),
            timeout,
        ]);
        const member = response.data?.member;
        if (member) {
            connectionStatus.value = member.connection_status || null;
            const nickname = member.connection_nickname;
            memberDisplayName.value = nickname
                ? `${member.name} (${nickname})`
                : member.name || null;
        }
    } catch {
        // Silently fail — the fallback CTA branch in the template renders
        // a link to notification.route so the user can still resolve manually.
    } finally {
        loading.value = false;
    }
};

const markNotificationRead = () => {
    if (!props.notification.id || props.notification.read_at) {
        return;
    }
    ApiService.apiCall(
        `/notifications/${props.notification.id}/mark-read`,
        'post'
    ).catch(() => {});
};

const ensureMembersLoaded = async () => {
    if (membersStore.walletId) {
        return;
    }

    const response = await ApiService.apiCall('/wallets/members', 'get');
    const data = response.data;

    membersStore.walletId = data.wallet?.id;
    membersStore.wallet = data.wallet;
    membersStore.members = data.members || [];
    membersStore.banks = data.banks || [];
    membersStore.users = data.wallet?.users || [];
};

const handleResolve = async () => {
    loadingResolve.value = true;

    try {
        await ensureMembersLoaded();

        const foundMember =
            membersStore.members.find(
                (m) => m.guid === props.notification.member_guid
            ) ?? { guid: props.notification.member_guid };

        localMember.value = foundMember;

        if (connectionStatus.value === 'CHALLENGED') {
            localShowChallengeModal.value = true;
        } else {
            localShowConnectModal.value = true;
        }
    } catch {
        // Fallback: open modal with stub so user can still attempt a fix
        localMember.value = { guid: props.notification.member_guid };
        localShowConnectModal.value = true;
    } finally {
        loadingResolve.value = false;
    }
};

const handleConnectModalClose = () => {
    localShowConnectModal.value = false;
};

const handleConnectSuccess = () => {
    localShowConnectModal.value = false;
    markNotificationRead();
    connectionStatus.value = 'CONNECTED';
    emit('resolved', props.notification.id);
};

const handleChallengeModalClose = () => {
    localShowChallengeModal.value = false;
};

const handleChallengeSuccess = () => {
    localShowChallengeModal.value = false;
    markNotificationRead();
    connectionStatus.value = 'CONNECTED';
    emit('resolved', props.notification.id);
};

const handleFallbackClick = () => {
    markNotificationRead();
    const route = props.notification.route;
    if (!route) {
        return;
    }

    // /go/{key} click-tracking URLs and absolute http(s):// URLs need
    // full-page navigation so the server-side 302 is followed by the
    // browser; Inertia's XHR-based router.visit() can't handle that.
    if (/^\/go\//.test(route) || /^https?:\/\//i.test(route)) {
        window.location.href = route;
        return;
    }

    router.visit(route);
};

onMounted(() => {
    fetchMemberStatus();
});
</script>
